import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { hrLearningDevelopmentAPI } from '../utils/api';
import { isAdmin } from '../utils/auth';
import HRAttachmentField from '../components/HRAttachmentField';
import './DashboardPage.css';

const notifyHrDataChanged = () => globalThis.dispatchEvent(new Event('hrDataChanged'));

const getFiscalYearStartYear = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const month = d.getMonth();
  const year = d.getFullYear();
  return month >= 9 ? year : year - 1;
};

const formatYearLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`;

const getStoredHrYear = () => {
  const current = getFiscalYearStartYear(new Date());
  try {
    const raw = localStorage.getItem('hrSelectedYear');
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : current;
  } catch {
    return current;
  }
};

const getCompletionColor = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return 'secondary';
  if (value >= 75) return 'success';
  if (value >= 40) return 'warning';
  return 'danger';
};

const formatCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return '0%';
  return `${value}%`;
};

const normalizeCompletionValue = (pct) => {
  const value = Number(pct);
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const MONTH_COLS = [
  { key: 'nov_25', label: 'Nov-25' },
  { key: 'dec_25', label: 'Dec-25' },
  { key: 'jan_26', label: 'Jan-26' },
  { key: 'feb_26', label: 'Feb-26' },
  { key: 'mar_26', label: 'Mar-26' },
  { key: 'apr_26', label: 'Apr-26' },
  { key: 'may_26', label: 'May-26' },
  { key: 'jun_26', label: 'Jun-26' },
  { key: 'jul_26', label: 'Jul-26' },
  { key: 'aug_26', label: 'Aug-26' },
  { key: 'sep_26', label: 'Sep-26' },
  { key: 'oct_26', label: 'Oct-26' },
];

const EMPTY_FORM = {
  actual_start_date: '',
  actual_end_date: '',
  completion_pct: '',
  responsibility: '',
  nov_25: '', dec_25: '', jan_26: '', feb_26: '', mar_26: '', apr_26: '',
  may_26: '', jun_26: '', jul_26: '', aug_26: '', sep_26: '', oct_26: '',
};

const SubParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  marks: PropTypes.number,
  description: PropTypes.string,
  guidelines: PropTypes.string,
  auditorDetails: PropTypes.string,
  subSubParameters: PropTypes.array
});

const ParameterPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  subParameters: PropTypes.arrayOf(SubParameterPropType).isRequired
});

export const INIT_PARAMETERS = [
  {
    id: 'training-business-linkage',
    name: 'Training - Business Linkage',
    subParameters: [
      {
        id: 'tbl-1',
        marks: 50,
        description: 'Training Programs to be aligned and linked to the Business Need and Objectives with clear tangible benefits. With due focus on - Safety, Governance, Technology, Digitalisation and Innovation.\n\nEntities should provide a Training Calendar & Mapping Matrix covering:\n● Training Calendar aligned to Org Need with measurable benefit assigned to the programs (50% of total training days)\n● Remaining - Training Progs aligned to the needs documented as per TNA (Perf Appraisal Process) linked to IDPs.\n● Focus on continuous Improvement Engine (TQM/Six Sigma/TPM etc). Improvement Projects\n● Mandatory Ethics, Cybersecurity, COC, UKBA & Human Rights Trainings. 100% Compliance & completion target',
        guidelines: 'Relevant details to be provided on this assertion.\n\nTraining calendar frozen at the company level (covering all executive population) at the start of the year. Training with focus on - Safety, Governance, Technology and Innovation.\n\nEvidence on training calendar being linked to organization needs, with measurable benefit defined for the trainings\n\nEmployee wise details of training to be provided.\n\nCompliance/training percentage to be shared for mandatory trainings.\n● (a) COC (b) UKBA & Human Rights - training once in a year to all.',
        auditorDetails: 'a) Training calendar of 2026-27 to be provided, evidence by when it was frozen\nb) Training MIS to be provided - Please map the same against all employees\nc) Employee wise details of training to be provided.\nd) Training with focus on - Safety, Governance, Technology and Innovation to be substantiated\ne) Focus on continuous Improvement Engine (TQM/Six Sigma/TPM etc). Improvement Projects-Initiatives to be in place\nf) To be substantiated that training Programs to be aligned and linked to the Business Need and Objectives with clear tangible benefits. With due focus on - Safety, Governance, Technology, Digitalisation and Innovation.\ng) There is a system of annual (a) COC training (b) UKBA & Human Rights and sign off from all executives - Share the % of population covered in 2026-27 along with the attendance sheets',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'actual-trainings',
    name: 'Actual Trainings',
    subParameters: [
      {
        id: 'at-1',
        marks: 30,
        description: 'All the employees to be covered under training. Training MIS to be provided.',
        guidelines: 'Relevant details to be provided on this assertion.\n\nTraining calendar frozen at the company level (covering all executive population) at the start of the year. Training with focus on - Safety, Governance, Technology and Innovation.\n\nEvidence on training calendar being linked to organization needs, with measurable benefit defined for the trainings\n\nEmployee wise details of training to be provided.\n\nCompliance/training percentage to be shared for mandatory trainings.\n● (a) COC (b) UKBA & Human Rights - training once in a year to all.',
        auditorDetails: 'a) Training calendar of 2026-27 to be provided, evidence by when it was frozen\nb) Training MIS to be provided - Please map the same against all employees\nc) Employee wise details of training to be provided.\nd) Training with focus on - Safety, Governance, Technology and Innovation to be substantiated\ne) Focus on continuous Improvement Engine (TQM/Six Sigma/TPM etc). Improvement Projects-Initiatives to be in place\nf) To be substantiated that training Programs to be aligned and linked to the Business Need and Objectives with clear tangible benefits.\ng) There is a system of annual (a) COC training (b) UKBA & Human Rights and sign off from all executives - Share the % of population covered in 2026-27 along with the attendance sheets',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'benchmarking-visits',
    name: 'Benchmarking Visits',
    subParameters: [
      {
        id: 'bv-1',
        marks: 50,
        description: 'Each business to have a structured calendar in place for Benchmarking Plant Visits - covering key talent/leaders who are part of key initiatives/ or are in key critical roles.\n\nReport to be shared on outcome/actioned item from benchmark and to be shared with Business EXCO.',
        guidelines: 'Scoring Guidelines:\n● Approved benchmarking Visit Calendar (Date of visit, agenda and scope of coverage for the visit, Key people conducting benchmarking, Benchmarking organization names) to be provided as evidence. Quarterly achievement plan to be assessed for scoring.\n  - Achieved 75% - 50 Marks\n  - Achieved 50% - 37 Marks\n  - Below 30% - 50% - 25 Marks\n● Proof to be furnished on report shared with BU EXCO including impact/outcome/actioned item report',
        auditorDetails: 'List of key talent/leaders identified through workshop initiatives, stars, leaders connect etc. who have conducted benchmark plant visits.\n\nPlease share % of leaders who have made such visits, along with the dates of such visits',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'training-roi-mis',
    name: 'Training ROI & MIS',
    subParameters: [
      {
        id: 'troi-1',
        description: '● System of ROI - Measurement, Training Effectiveness is in place.\n● Monthly Training MIS should be in place. (Benefit on account of training programmes should be documented)',
        guidelines: 'Documentation to be provided on the ROI calculation mechanism and actual ROI of the trainings.\n\nEntities to demonstrate how training has helped in improvement of performance of the individual and organization.\n\nSystem of measurement of training in place? Evidence how it is being measured',
        auditorDetails: 'Documentation to be provided on the ROI calculation mechanism and actual ROI of the trainings.\n\nEntities to demonstrate how training has helped in improvement of performance of the individual and organization.',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'coaching-program',
    name: 'Coaching Program',
    subParameters: [
      {
        id: 'cp-1',
        description: 'Identifying and assigning Executive Coaches to the Business EXCO/MIP members. To run structured program.\n\n● IDP of each EXCO members (with details like strengths & development areas, key activities, timelines) as evidence to be presented.\n● Sign off from BU Head on Executive coaching program for all cases',
        guidelines: '● IDP of each EXCO members (with details like strengths & development areas, key activities, timelines) as evidence to be presented.\n● Sign off from BU Head on Executive coaching program for all cases',
        auditorDetails: 'List of members of BU ExCo - mapping with the coaches assigned to them.\n\nEvidences of the coachings being imparted.',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'induction-familization',
    name: 'Induction & Familization Program and Code of Conduct Training',
    subParameters: [
      {
        id: 'if-a',
        label: 'A',
        marks: 30,
        description: 'Structured induction program is in place for Freshers and Lateral joinees. The familiarization program to cover business overview besides all required policy related aspects.',
        guidelines: '● Structured induction program organized.\n● Joining kit provided covering all relevant documents.\n● Record documented.',
        auditorDetails: 'Share the induction kit.\n\nConfirmation that induction was given to all the new joinees - Share the list of new joinees between Nov\'25 - Oct\'26, we can ask for specific samples.',
        subSubParameters: [],
      },
      {
        id: 'if-b',
        label: 'B',
        marks: 30,
        description: 'Feedback Survey for Lateral Hires regarding onboarding and other touchpoint feedbacks once induction is complete. Feedback report to be submitted to Central Leadership Team.',
        guidelines: '● Feedback survey conducted for lateral hires post induction.\n● Feedback report submitted to Central Leadership Team.',
        auditorDetails: 'Share the induction kit.\n\nConfirmation that induction was given to all the new joinees - Share the list of new joinees between Nov\'25 - Oct\'26, we can ask for specific samples.',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'training-stars-managers',
    name: 'Training for Stars / Managers (HODs)',
    subParameters: [
      {
        id: 'tsm-a',
        label: 'A',
        marks: 20,
        description: 'Minimum one Leadership Program for Manager as per IDP annually. (Particularly in Grade M4 & above)',
        guidelines: 'a) Evidence on leadership development programme conducted for managers.\n\nMIS to be maintained and documented as part of IDP management.',
        auditorDetails: 'a) Please map list of programs partaken by stars on an annual basis (last 12 months) (Programs do not include mandatory trainings)',
        subSubParameters: [],
      },
      {
        id: 'tsm-b',
        label: 'B',
        marks: 20,
        description: 'Minimum two-man days of relevant training / workshop for \'Managers (HODs)\' on Managerial Effectiveness; on Yearly basis. (to be suitably linked to 360 DF)\n\nNote: Managers with Star/Hi-Po Status to only receive Managerial Effectiveness; on Yearly basis.',
        guidelines: 'b) MIS to be maintained and documented for record and ref.\n\nROI to be presented in respective business EXCO meeting.',
        auditorDetails: 'b) Please map list of training days done by managers (HODs) on a yearly basis. Please map the same against list of entire HODs.\n\nRefer Annexure 6',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'skill-matrix',
    name: 'Skill Matrix Exercise',
    subParameters: [
      {
        id: 'sm-1',
        description: 'Skill Matrix Process to be in place for Below M4 grade Population. Radar diagram - equivalent to be in place. Corrective steps to be documented which could be Training, Rotation, Separation etc\n\nProgress to be evident and documented. Entities to also frame IDPs based on Skill Matrix Exercise. Exercise can be done once in 3 years.',
        guidelines: 'Progress to be evident and documented.\n\nEntities to also frame IDPs based on Skill Matrix Exercise.\n\nExercise can be done once in 3 years.',
        auditorDetails: 'Please share skill matrix in place\n\nShare the evidence of corrective actions taken for those where gaps are identified',
        subSubParameters: [],
      },
    ],
  },
  {
    id: 'anchoring-mentoring-idp',
    name: 'Anchoring-Mentoring; IDP Institutionalisation and 360 DF',
    subParameters: [
      {
        id: 'ami-a',
        label: 'A',
        marks: 25,
        description: 'Anchoring and mentoring of executive population (including Laterals, Expats & freshers):',
        guidelines: 'A) Quarterly report to be provided on the progress for ref/audit purpose.',
        auditorDetails: 'a) Please share progress report of V connect stating population covered on a quarterly basis.',
        subSubParameters: [],
      },
      {
        id: 'ami-b',
        label: 'B',
        marks: 30,
        description: 'Digital Tracking - Ensuring Right Anchors & periodical connects',
        guidelines: 'B) Digital Tracking: Entity to provide report on tracking of anchoring sessions & effectiveness',
        auditorDetails: 'b) Evidence to be provided that all stars and leaders identified through various leadership identification programmes have IDPs in place and anchoring sessions are taking place periodically. The same should be signed off by SBU heads and IDP progress to be reviewed on a half yearly basis.\n\nDigital tracking (Please share the list of leaders identified through such programmes, post that we will provide samples). Digital Tracking to be done through any platform - till platform is developed can be tracked through excel dashboard (Report of either to be shared)',
        subSubParameters: [],
      },
      {
        id: 'ami-c',
        label: 'C',
        marks: 25,
        description: 'All identified \'Stars\' and \'leaders\' through various leadership identification programmes (including V-Reach, V-build, V-Lead etc.) to have IDP in place and signed-off by the SBU Heads - to be reviewed & measured on half yearly basis.',
        guidelines: 'C) IDP:\n● Signed-Off document and half yearly Review & Measurement Progress Report.\n● Entity to demonstrate mentoring / grooming and progression.',
        auditorDetails: 'b) Evidence to be provided that all stars and leaders identified through various leadership identification programmes have IDPs in place and anchoring sessions are taking place periodically. The same should be signed off by SBU heads and IDP progress to be reviewed on a half yearly basis.',
        subSubParameters: [],
      },
      {
        id: 'ami-d',
        label: 'D',
        marks: 25,
        description: '360 Degree Feedback system for Business/SBU EXCO once a year. Through this also focus on \'Managerial Effectiveness\'.',
        guidelines: 'D) 360 DF:\n● Effectively running the process and sample record as evidence.',
        auditorDetails: 'c) List of HODs as on Oct\'25 - post which we will ask for sample of 360 degree feedback. In case entity doesn\'t have 360 degree framework in place, please let us know',
        subSubParameters: [],
      },
      {
        id: 'ami-freshers-a',
        label: 'Freshers A',
        marks: 10,
        description: 'Anchoring for Freshers: All freshers (GETs, CAs, MBAs) in their first 12 months have a Mentor assigned as per Mentoring Program Initiative. Existence of a system of review and effective measurement tool in place to assess the success of this programme',
        guidelines: 'Anchoring for Freshers:\nCopy of digital report to be provided of mentor mentee meetings.\n\nAdequate details to be provided regarding monitoring of mentoring programme by HR and requisite sign off of meetings.',
        auditorDetails: 'Anchoring for Freshers:\nPlease share evidence that all freshers and stars have a mentor in place through mentor programme initiatives.\n\nPlease share evidence of meetings and existence of a system of review on digital platform and effective measurement tool in place to assess the success of this programme.',
        subSubParameters: [],
      },
      {
        id: 'ami-freshers-b',
        label: 'Freshers B',
        marks: 10,
        description: 'Anchoring for Freshers: Digital tracking of mentor mentee connect through online / digital portal',
        guidelines: 'Anchoring for Freshers:\nCopy of digital report to be provided of mentor mentee meetings.\n\nAdequate details to be provided regarding monitoring of mentoring programme by HR and requisite sign off of meetings.',
        auditorDetails: 'Anchoring for Freshers:\nPlease share evidence that all freshers and stars have a mentor in place through mentor programme initiatives.\n\nPlease share evidence of meetings and existence of a system of review on digital platform and effective measurement tool in place to assess the success of this programme.',
        subSubParameters: [],
      },
    ],
  },
];

// Format slash-separated names for display
const formatResponsibility = (val) =>
  val ? val.split('/').map((s) => s.trim()).filter(Boolean).join(' / ') : '';

// ── Inline responsibility editor ──────────────────────────────────────────
const ResponsibilityField = ({ value, onSave, readOnly }) => {
  const admin = isAdmin();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
      setEditing(false);
    } catch (e) {
      const message =
        typeof e === 'string'
          ? e
          : e?.message || e?.data?.message || 'Failed to save responsibility.';
      setError(message);
      setEditing(true);
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => { setDraft(value || ''); setEditing(false); setError(''); };
  const display = formatResponsibility(value);

  if (!admin || readOnly) {
    return (
      <div className="mb-3">
        <strong className="hr-responsibility">Responsibility: </strong>
        <span className="hr-responsibility">{display || <em className="text-muted">Not set</em>}</span>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <strong className="hr-responsibility">Responsibility: </strong>
      {editing ? (
        <span className="d-inline-flex align-items-center gap-1 ms-1 flex-wrap">
          <Form.Control
            size="sm"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Name1 / Name2"
            className="hr-inline-input"
            autoFocus
          />
          <Button variant="success" size="sm" className="py-0 px-1" onClick={confirm} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <FiCheck size={13} />}
          </Button>
          <Button variant="outline-secondary" size="sm" className="py-0 px-1" onClick={cancel} disabled={saving}>
            <FiX size={13} />
          </Button>
          {error && <small className="text-danger ms-2">{error}</small>}
        </span>
      ) : (
        <span className="hr-responsibility">
          {display || <em className="text-muted">Not set</em>}
          <Button variant="link" size="sm" className="p-0 ms-2 d-inline-flex align-items-center gap-1" onClick={() => setEditing(true)} title="Edit responsibility">
            <FiEdit2 size={13} />
            <span>Edit</span>
          </Button>
        </span>
      )}
    </div>
  );
};

// ── Sub-parameter form ────────────────────────────────────────────────────
const SubParameterForm = ({ subParam, parameterId, departmentId, fyStartYear, readOnly }) => {
  const [form, setForm]       = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [hasRecord, setHasRecord] = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [alert, setAlert]         = useState(null);
  const [attachments, setAttachments] = useState([]);

  const hasSubSub = subParam.subSubParameters && subParam.subSubParameters.length > 0;

  const fetchRecord = useCallback(async () => {
    if (!departmentId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await hrLearningDevelopmentAPI.getRecord(departmentId, parameterId, subParam.id, fyStartYear);
      if (res?.data) {
        const d = res.data;
        setForm({
          actual_start_date: d.actual_start_date || '',
          actual_end_date:   d.actual_end_date   || '',
          completion_pct: d.completion_pct !== null && d.completion_pct !== undefined ? String(d.completion_pct) : '',
          responsibility: d.responsibility || '',
          nov_25: d.nov_25 || '', dec_25: d.dec_25 || '', jan_26: d.jan_26 || '',
          feb_26: d.feb_26 || '', mar_26: d.mar_26 || '', apr_26: d.apr_26 || '',
          may_26: d.may_26 || '', jun_26: d.jun_26 || '', jul_26: d.jul_26 || '',
          aug_26: d.aug_26 || '', sep_26: d.sep_26 || '', oct_26: d.oct_26 || '',
        });
        setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
        setHasRecord(true);
      } else {
        setForm((prev) => ({ ...prev, responsibility: '' }));
        setAttachments([]);
        setHasRecord(false);
      }
    } catch {
      setForm((prev) => ({ ...prev, responsibility: '' }));
      setAttachments([]);
      setHasRecord(false);
    } finally { setLoading(false); }
  }, [departmentId, parameterId, subParam.id, fyStartYear]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const formRef = React.useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  const saveResponsibility = async (val) => {
    const f = formRef.current;
    try {
      await hrLearningDevelopmentAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id,
        actual_start_date: f.actual_start_date || null, actual_end_date: f.actual_end_date || null,
        completion_pct: f.completion_pct !== '' ? f.completion_pct : null,
        responsibility: val || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: f[key] || null }), {}),
      });
      setForm((prev) => ({ ...prev, responsibility: val }));
      setHasRecord(true);
      notifyHrDataChanged();
    } catch (e) {
      console.error('saveResponsibility failed:', e);
      throw e;
    }
  };

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true); setAlert(null);
    try {
      await hrLearningDevelopmentAPI.upsertRecord({
        department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id,
        actual_start_date: form.actual_start_date || null, actual_end_date: form.actual_end_date || null,
        completion_pct: form.completion_pct !== '' ? form.completion_pct : null,
        responsibility: form.responsibility || null,
        ...MONTH_COLS.reduce((acc, { key }) => ({ ...acc, [key]: form[key] || null }), {}),
      });
      setAlert({ type: 'success', message: 'Saved successfully.' });
      setHasRecord(true); setEditMode(false);
      notifyHrDataChanged();
    } catch (e) {
      const message =
        typeof e === 'string'
          ? e
          : e?.message || e?.data?.message || 'Failed to save. Please try again.';
      setAlert({ type: 'danger', message });
    }
    finally { setSaving(false); }
  };

  const handleCancel = () => { fetchRecord(); setEditMode(false); setAlert(null); };
  const displayVal = (val) => val || <span className="text-muted">—</span>;

  const handleUpload = async (files) => {
    if (readOnly) return;
    const fd = new FormData();
    files.forEach((file) => fd.append('files', file));
    fd.append('department_id', departmentId);
    fd.append('year_start', String(fyStartYear));
    fd.append('parameter_id', parameterId);
    fd.append('sub_parameter_id', subParam.id);
    const res = await hrLearningDevelopmentAPI.uploadAttachment(fd);
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    setHasRecord(true);
    notifyHrDataChanged();
  };

  const handleDeleteAttachment = async (attachmentIndex) => {
    if (readOnly) return;
    const res = await hrLearningDevelopmentAPI.deleteAttachment({ department_id: departmentId, year_start: fyStartYear, parameter_id: parameterId, sub_parameter_id: subParam.id, attachment_index: attachmentIndex });
    setAttachments(Array.isArray(res.attachments) ? res.attachments : []);
    notifyHrDataChanged();
  };

  const handleDownload = (attachmentIndex) => {
    hrLearningDevelopmentAPI.downloadAttachment(departmentId, parameterId, subParam.id, attachmentIndex, fyStartYear).then((blob) => {
      const url = globalThis.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a'); a.href = url; a.download = attachments[attachmentIndex]?.name || 'attachment';
      document.body.appendChild(a); a.click(); a.remove(); globalThis.URL.revokeObjectURL(url);
    });
  };

  const formFields = (
    <>
      <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="hr-form-label">Actual Start Date</Form.Label>
            <Form.Control type="date" name="actual_start_date" value={form.actual_start_date} onChange={handleChange} size="sm" disabled={readOnly} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="hr-form-label">Actual End Date</Form.Label>
            <Form.Control type="date" name="actual_end_date" value={form.actual_end_date} onChange={handleChange} size="sm" disabled={readOnly} />
          </Form.Group>
        </Col>
        {!hasSubSub && (
          <Col md={4}>
            <Form.Group>
              <Form.Label className="hr-form-label">% Completion</Form.Label>
              <Form.Control type="number" name="completion_pct" value={form.completion_pct} onChange={handleChange} min={0} max={100} step={0.01} placeholder="0 – 100" size="sm" disabled={readOnly} />
            </Form.Group>
          </Col>
        )}
      </Row>
      <Row className="g-2 mb-3">
        {MONTH_COLS.map(({ key, label }) => (
          <Col key={key} xs={6} sm={4} md={3} lg={2}>
            <Form.Group>
              <Form.Label className="hr-form-label-sm">{label}</Form.Label>
              <Form.Control type="text" name={key} value={form[key]} onChange={handleChange} size="sm" placeholder={label} disabled={readOnly} />
            </Form.Group>
          </Col>
        ))}
      </Row>
      <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
    </>
  );

  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        {'label' in subParam && (
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge bg-primary hr-label-badge">{subParam.label}</span>
            {subParam.marks && <span className="badge hr-marks-badge">{subParam.marks} Marks</span>}
          </div>
        )}
        <div className="mb-3">
          <strong className="hr-section-heading">Parameter</strong>
          <p className="mb-0 hr-section-text">{subParam.description}</p>
        </div>
        <div className="mb-3">
          <strong className="hr-section-heading">Guidelines for Scoring Matrix</strong>
          <pre className="mt-1 mb-0 hr-section-pre">{subParam.guidelines}</pre>
        </div>
        <div className="mb-3">
          <strong className="hr-section-heading">Details Required by Auditors</strong>
          <pre className="mt-1 mb-0 hr-section-pre">{subParam.auditorDetails}</pre>
        </div>
        <hr />
        {loading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : !hasRecord ? (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>
              {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
            </Button>
          </Form>
        ) : !editMode ? (
          <>
            <ResponsibilityField value={form.responsibility} onSave={saveResponsibility} readOnly={readOnly} />
            <Row className="g-3 mb-3">
              <Col md={4}><div className="hr-detail-row"><strong>Actual Start Date:</strong> {displayVal(form.actual_start_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row"><strong>Actual End Date:</strong> {displayVal(form.actual_end_date)}</div></Col>
              <Col md={4}><div className="hr-detail-row"><strong>% Completion:</strong> <span className={`text-${getCompletionColor(form.completion_pct || 0)}`}>{formatCompletionValue(form.completion_pct)}</span></div></Col>
            </Row>
            <Row className="g-2 mb-3">
              {MONTH_COLS.map(({ key, label }) => (
                <Col key={key} xs={6} sm={4} md={3} lg={2}>
                  <div className="hr-month-detail">
                    <div className="text-muted">{label}</div>
                    <div>{displayVal(form[key])}</div>
                  </div>
                </Col>
              ))}
            </Row>
            <HRAttachmentField attachments={attachments} onUpload={handleUpload} onDelete={handleDeleteAttachment} onDownload={handleDownload} disabled={readOnly} />
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            {!readOnly && (
              <Button variant="outline-primary" size="sm" onClick={() => { setAlert(null); setEditMode(true); }}>
                <FiEdit2 size={13} className="me-1" />Edit
              </Button>
            )}
          </>
        ) : (
          <Form onSubmit={handleSubmit}>
            {formFields}
            {alert && <Alert variant={alert.type} className="py-2 mb-3 hr-alert">{alert.message}</Alert>}
            <div className="d-flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={saving || readOnly}>
                {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving...</> : 'Submit'}
              </Button>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleCancel} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

// Compute effective completion % for a parameter given a map of DB records
const computeParameterCompletion = (param, recordMap) => {
  const subPctValues = [];
  param.subParameters.forEach((sp) => {
    const hasSubSub = sp.subSubParameters && sp.subSubParameters.length > 0;
    if (!hasSubSub) {
      const rec = recordMap[sp.id];
      if (rec && rec.completion_pct !== null && rec.completion_pct !== undefined) {
        subPctValues.push(parseFloat(rec.completion_pct));
      }
    } else {
      const sspPcts = sp.subSubParameters
        .map((ssp) => recordMap[`${sp.id}:${ssp.id}`])
        .filter((r) => r && r.completion_pct !== null && r.completion_pct !== undefined)
        .map((r) => parseFloat(r.completion_pct));
      if (sspPcts.length > 0) {
        subPctValues.push(sspPcts.reduce((a, b) => a + b, 0) / sspPcts.length);
      }
    }
  });
  if (subPctValues.length === 0) return null;
  const avg = subPctValues.reduce((a, b) => a + b, 0) / subPctValues.length;
  return Math.round(avg * 10) / 10;
	};

SubParameterForm.propTypes = {
  subParam: SubParameterPropType.isRequired,
  parameterId: PropTypes.string.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

// ── Parameter Tile ────────────────────────────────────────────────────────
const ParameterTile = ({ param, departmentId, fyStartYear, onClick }) => {
  const [responsibility, setResponsibility] = useState('');
  const [completionPct, setCompletionPct] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handleHrDataChanged = () => setRefreshKey((value) => value + 1);
    globalThis.addEventListener('hrDataChanged', handleHrDataChanged);
    return () => globalThis.removeEventListener('hrDataChanged', handleHrDataChanged);
  }, []);

  useEffect(() => {
    if (!departmentId || param.subParameters.length === 0) return;
    hrLearningDevelopmentAPI.getParameterRecords(departmentId, param.id, fyStartYear)
      .then((res) => {
        const records = res?.data || [];
        if (records.length === 0) {
          setResponsibility('');
          setCompletionPct(0);
          return;
        }
        const names = [...new Set(records.map((r) => r.responsibility).filter(Boolean))].join(' / ');
        setResponsibility(names);
        const recordMap = {};
        records.forEach((r) => { recordMap[r.sub_parameter_id] = r; });
        setCompletionPct(computeParameterCompletion(param, recordMap));
      })
      .catch(() => {
        setResponsibility('');
        setCompletionPct(0);
      });
  }, [departmentId, param, refreshKey, fyStartYear]);

  const normalizedCompletionPct = normalizeCompletionValue(completionPct);
  const pctColor = getCompletionColor(normalizedCompletionPct);

  return (
    <Card className="shadow-sm h-100 hr-tile-card" onClick={onClick}>
      {responsibility && (
        <div className="hr-tile-responsibility">
          {responsibility}
        </div>
      )}
      <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
        <h6 className="mb-0 text-center">{param.name}</h6>
        <div className="mt-3 w-100">
          <div className="d-flex justify-content-between mb-1 hr-tile-meta">
            <span className="text-muted">Completion</span>
            <span className={`hr-tile-percent text-${pctColor}`}>{formatCompletionValue(normalizedCompletionPct)}</span>
          </div>
          <div className="hr-progress" style={{ '--hr-progress-width': `${normalizedCompletionPct}%` }}>
            <div className={`hr-progress-fill hr-progress-${pctColor}`} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
	};

ParameterTile.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fyStartYear: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired
};

ResponsibilityField.propTypes = {
  value: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

// ── Parameter Detail ──────────────────────────────────────────────────────
const ParameterDetail = ({ param, departmentId, onBack, onBackToMain, fyStartYear, readOnly }) => (
  <Container fluid className="dashboard-page py-4">
    <div className="d-flex align-items-center gap-2 mb-4">
      <Button variant="outline-secondary" size="sm" onClick={onBack}>
        <FiArrowLeft size={14} className="me-1" />Back
      </Button>
      <Button variant="outline-primary" size="sm" onClick={onBackToMain}>
        <FiArrowLeft size={14} className="me-1" />HR Main Dashboard
      </Button>
      <h5 className="mb-0">{param.name}</h5>
    </div>
    {param.subParameters.length === 0 ? (
      <p className="text-muted">No sub-parameters defined yet.</p>
    ) : (
      param.subParameters.map((sp) => (
        <SubParameterForm
          key={sp.id}
          subParam={sp}
          parameterId={param.id}
          departmentId={departmentId}
          fyStartYear={fyStartYear}
          readOnly={readOnly}
        />
      ))
    )}
  </Container>
);

ParameterDetail.propTypes = {
  param: ParameterPropType.isRequired,
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func.isRequired,
  onBackToMain: PropTypes.func.isRequired,
  fyStartYear: PropTypes.number.isRequired,
  readOnly: PropTypes.bool
};

// ── Main page ─────────────────────────────────────────────────────────────
const HRLearningDevelopmentDashboardPage = ({ departmentId = null }) => {
  const navigate = useNavigate();
  const [fyStartYear, setFyStartYear] = useState(getStoredHrYear());
  const [selectedParam, setSelectedParam] = useState(null);

  const currentFy = getFiscalYearStartYear(new Date());
  const readOnly = !isAdmin() && fyStartYear !== currentFy;

  useEffect(() => {
    try { localStorage.setItem('hrSelectedYear', String(fyStartYear)); } catch {}
  }, [fyStartYear]);

  const goToHrMainDashboard = () => {
    localStorage.setItem('selectedProject', JSON.stringify({
      id: 'hr-main-dashboard',
      name: 'HR Main Dashboard',
      departmentId,
    }));
    globalThis.dispatchEvent(new Event('projectChanged'));
    navigate('/dashboard', { replace: true });
  };

  if (selectedParam) {
    return (
      <ParameterDetail
        param={selectedParam}
        departmentId={departmentId}
        onBack={() => setSelectedParam(null)}
        onBackToMain={goToHrMainDashboard}
        fyStartYear={fyStartYear}
        readOnly={readOnly}
      />
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      <div className="d-flex align-items-center gap-2 mb-4">
        <Button variant="outline-primary" size="sm" onClick={goToHrMainDashboard}>
          <FiArrowLeft size={14} className="me-1" />HR Main Dashboard
        </Button>
        <h5 className="mb-0">Learning &amp; Development</h5>
        <div className="ms-auto d-flex align-items-center gap-2">
          {readOnly && <span className="text-muted small">Read-only (previous year)</span>}
          <Form.Select size="sm" value={fyStartYear} onChange={(e) => setFyStartYear(Number(e.target.value))} style={{ width: 140 }}>
            {[currentFy - 1, currentFy, currentFy + 1].map((y) => (
              <option key={y} value={y}>{formatYearLabel(y)}</option>
            ))}
          </Form.Select>
        </div>
      </div>
      <Row className="g-3 row-cols-1 row-cols-md-2 row-cols-lg-3">
        {INIT_PARAMETERS.map((param) => (
          <Col key={param.id}>
            <ParameterTile
              param={param}
              departmentId={departmentId}
              fyStartYear={fyStartYear}
              onClick={() => setSelectedParam(param)}
            />
          </Col>
        ))}
      </Row>
    </Container>
	);
};

HRLearningDevelopmentDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HRLearningDevelopmentDashboardPage;
